'use client';

import { Fragment } from 'react';
import styles from './ui.module.css';
import { SiteFooter } from './site-footer';
import { SiteNav } from './site-nav';
import { SceneWipe } from './motion';
import { WorkNav } from './work-nav';
import { workplaceFor, WORK_NAV, type NavSection, type NavStanding } from '@goodbuild/rules/site';
import type { SiteId } from '@goodbuild/rules/sites';

/**
 * The chrome around a page: the bar, the rail, the footer, and the shell that
 * holds them apart.
 *
 * **The bar has been one bar since #62 and the chrome was still two.** `topNav`
 * is one function, `SiteNav` and `SiteFooter` are one component each, and
 * `site-chrome.test.ts` proves the two applications produce an identical list
 * for the same account. What was not shared is everything *around* those
 * calls — the React app assembled its shell in `Page` and the storefront
 * assembled a second one by hand in `Base.astro` — and a shared component
 * called from two hand-written assemblies is a shared component in the way two
 * copies of a recipe are one dinner.
 *
 * Everything the storefront's copy got wrong is the same mistake: it did not
 * know about something the React app was getting for free. `signedIn` was the
 * literal `false`, so a signed-in player was offered `Sign in` on every page of
 * the catalogue. `standing` was absent, so a studio browsing the shop lost the
 * link to its own portal one hostname from where it worked. `appOrigin` was
 * absent, so that sign-in link 404ed. Each was found separately, months apart,
 * by somebody noticing.
 *
 * The one that was never found is the shell. `Page` is a flex column with the
 * main region growing, so the footer sits at the bottom of the window on a
 * short page; the storefront had no such element, so on `/404` and on an empty
 * catalogue its footer floated up under two lines of text with the rest of the
 * window empty below it — which reads as a rendering fault, on the page whose
 * whole job is to look deliberate.
 *
 * So there is one assembly and both applications render it. What still differs
 * between them is a prop with a reason written next to it, rather than two
 * files that have to be remembered together.
 *
 * **Props still win over context**, which is what `SiteNav` has always done and
 * what let the storefront hand over four booleans it read off a cookie while
 * the React app installed `AccountProvider` once. Only the app is left, so in
 * practice the context answers everywhere — the precedence stays because it
 * costs nothing and a surface that has to state a standing outright should be
 * able to.
 *
 * The origin props are gone with the storefront: `appOrigin` and `storeOrigin`
 * existed to tell this chrome where the *other* Worker was, and there is not
 * one. See the note where `chromeHref` used to be, in `../site`.
 */
export interface SiteChromeProps {
  /** Which of the three sites serves this page. Chooses the bar and the footer. */
  site: SiteId;
  /**
   * Whose page this is, when it is not the site's own. Chooses the ground and
   * the rail.
   *
   * Brand guidelines section 04: one channel per surface. Usually this is the
   * site, which is why it defaults to it — a creator's public page is the
   * exception, served by the store because a *player* reads it, and painted in
   * the creators' colours because section 04 says a creator surface is Hot
   * throughout.
   */
  space?: SiteId | undefined;
  /** The path being viewed, so the bar and the rail can mark it. */
  currentPath?: string | undefined;
  signedIn?: boolean | undefined;
  standing?: NavStanding | undefined;
  /** The account widget. A surface with no React node to give leaves it out. */
  account?: React.ReactNode | undefined;
  /**
   * What to call this visitor, for the surface that cannot build a widget.
   *
   * The storefront's half of the same question `account` answers, and it is a
   * plain string for exactly that reason: an `.astro` fragment is not a React
   * node, and the store's bar therefore said the literal word "Account" while
   * the React app's said the person's name (#113). Read from the `gb_name`
   * cookie the app writes for itself across its four hostnames; ignored when `account`
   * is given, because a widget already contains the name.
   *
   * `null` as well as `undefined`, so the storefront's `string | null` arrives
   * unconverted — see `SiteNav.accountName`, where the distinction between
   * "nobody told us" and "there is no name" is written out.
   */
  accountName?: string | null | undefined;
  /**
   * What the rail holds, when the audience's standing list is not the answer.
   *
   * Defaults to `WORK_NAV` for whichever workplace this page is in, and to
   * nothing on the store. An application passes this when the rail depends on
   * something only it knows — which organisation is being looked at (#88),
   * whether the creator has onboarded at all (#90), whether this is actually
   * staff (#111) — and an empty list draws no rail at all rather than an empty
   * column.
   */
  sections?: readonly NavSection[] | undefined;
  /** Rendered edge to edge, between the bar and the padded content. */
  bleed?: React.ReactNode;
  backdrop?: React.ReactNode;
  /**
   * Whether the chrome supplies the content column, or the page brings its own.
   *
   * True in the React app, where sixteen routes render `Page` and none of them
   * should be thinking about a gutter. **False on the storefront, and that is
   * the one thing still different between the two.** Every public page there
   * declares its own `.page` in a scoped `<style>` block — with its own
   * padding, which `/legal` in particular has tuned for a document rather than
   * for a shop — so a column here would be a second one wrapping the first:
   * two gutters, two measures, and a visibly narrower page.
   *
   * Collapsing those twelve blocks into this one is worth doing and is
   * deliberately not done here. It is a change to how every public page is
   * spaced, and this is a change to what draws the chrome; doing both at once
   * means neither can be reverted on its own.
   */
  column?: boolean;
  children?: React.ReactNode;
}

export function SiteChrome({
  site,
  space,
  currentPath,
  signedIn,
  standing,
  account,
  accountName,
  sections,
  bleed,
  backdrop,
  column = true,
  children,
}: SiteChromeProps): React.ReactElement {
  const ground = space ?? site;

  /*
   * The rail, on the surfaces that are a workplace.
   *
   * `ground` and not `site` is the load-bearing half, and it is why the
   * storefront gets no rail without having to ask for one: `site` is which host
   * answered, which in the React app is `store` for every page it serves, and
   * `ground` is whose page this is — the question a navigation of that
   * audience's screens is the answer to.
   *
   * **The ground stopped being the whole answer at #111.** This used to ask
   * `isWorkSite(ground)`, which is `false` on `/admin` — the staff panel
   * belongs to no audience, so `audienceForPath` returns null for it and the
   * ground falls back to the host, which is `store` for anybody who arrived on
   * `app.goodbuild.cc`. So no rail was drawn there, and the resolver the app
   * hands down in `sections` was never even called: eleven staff screens with
   * no navigation between them, and no way for the application to supply one.
   *
   * `workplaceFor` is the same question asked so that the path can answer it
   * too. The ground still decides on every other page — `/onboarding` renders
   * either audience and only the page knows which — and `null` is still how the
   * store says it has no rail, which is why this is a nullable lookup rather
   * than a length check.
   */
  const place = workplaceFor(ground, currentPath);
  const groups = sections ?? (place ? WORK_NAV[place] : []);
  const rail =
    place && groups.length > 0 ? (
      <WorkNav site={place} sections={groups} currentPath={currentPath} />
    ) : null;

  /**
   * Which page this is, for the two pieces of arrival motion below.
   *
   * The pathname only: `?org=…` and a fragment are the same page seen
   * differently, and re-running a scene change because somebody switched a
   * filter would make the transition a tic rather than an event.
   */
  const scene = currentPath?.split(/[?#]/, 1)[0] ?? '/';

  /*
    `.pageMain` travels with the column and not without it.

    It is a flex column with a gap, which is the spacing between a page's
    sections — a property of the column this component supplies. A page that
    brings its own brings that too, and adding a second one here would insert a
    gap between top-level elements that a storefront page had deliberately put
    flush against each other.

    **`key` is what makes the page deal itself in on every navigation.** The
    stagger in `ui.module.css` is a CSS animation, and a CSS animation runs when
    an element is inserted — so a route swap that React resolves by editing the
    existing children in place produces no arrival at all, which is most of them
    and is silent when it happens. Keying the element on the path means every
    navigation is a fresh subtree and the animation is guaranteed to play.

    The cost is honest and small: page state is discarded on navigation, which
    is what a navigation does anyway. The one case where it is doing real work
    is a same-route parameter change — `/g/one` to `/g/two` — where React would
    otherwise keep the DOM and the reader would arrive at a different game with
    no sign that anything had happened.
  */
  const main = (
    <main key={scene} {...(column ? { className: styles.pageMain } : {})}>
      {children}
    </main>
  );

  return (
    /*
      The shell: a flex column with the middle growing, so the footer sits at
      the bottom of a short page instead of halfway up it.

      `data-gb-space` here as well as on `<html>` in the storefront, and only
      here in the React app. Restating the same value nests one `[data-gb-space]`
      block inside an identical one, which resolves to the same tokens — and it
      is what lets a page take one site's chrome and another's ground, which is
      the whole reason `space` exists.
    */
    <div
      className={backdrop ? `${styles.shell} ${styles.shellBackdropped}` : styles.shell}
      data-gb-space={ground}
    >
      {backdrop}
      {/*
        The scene change, inside the shell rather than at the root of the
        document, and that is where it has to be: the blade is `--gb-accent`,
        and `--gb-accent` is whatever `[data-gb-space]` on this element resolves
        it to. Outside it the sweep would be the `:root` default — blue — on a
        creator's page and on the store alike.

        It is scenery: fixed, `pointer-events: none`, `aria-hidden`, and never
        covering the window. See `motion.module.css`.
      */}
      <SceneWipe scene={scene} />

      {/*
        No bar wrapper here. `SiteNav` draws its own, because it used to be
        drawn by the caller and the storefront — which rendered the component
        directly — got none of it: no ground, no border, no gutter, just a row
        of links flush against the window edge. A component that looks different
        depending on who remembered to wrap it is not a shared component.
      */}
      <SiteNav
        site={site}
        currentPath={currentPath}
        signedIn={signedIn}
        standing={standing}
        account={account}
        accountName={accountName}
      />

      {/*
        Keyed for the same reason `<main>` is, and for one case in particular.

        The hero settles on arrival — see `.heroArt` in `store.module.css` — and
        that is a mount animation. Every route swap replaces this node anyway
        except the one that matters most: `/g/one` to `/g/two` renders the same
        `StoreHero` in the same position, so React keeps the element and swaps
        the image inside it, and a reader clicking through the catalogue would
        get a new game with no sign that the page had turned over.
      */}
      <Fragment key={scene}>{bleed}</Fragment>

      {/*
        `<main>` is the content column on a work surface and the whole of it
        otherwise, so the rail sits beside it rather than inside: a navigation
        is not part of the main content of the document, and nesting it would
        announce it as one to anybody moving between landmarks.
      */}
      <div className={column ? `${styles.page} ${styles.shellMain}` : styles.shellMain}>
        {rail ? (
          <div className={styles.workBody}>
            {rail}
            {main}
          </div>
        ) : (
          main
        )}
      </div>

      <SiteFooter site={site} />
    </div>
  );
}
